import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Home, Dumbbell, Droplets, Trophy, User, LogOut, Menu, X,
  Flame, Target, Calendar, TrendingUp, Plus, Check, Heart, Utensils
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { progressApi, fitnessApi, achievementsApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import ProgressCharts from '../components/Dashboard/ProgressCharts';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [hydration, setHydration] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashData, hydData, progData, achData] = await Promise.all([
        progressApi.getDashboard(),
        progressApi.getTodayHydration(),
        fitnessApi.getPrograms(),
        achievementsApi.getAll()
      ]);
      setDashboard(dashData);
      setHydration(hydData);
      setPrograms(progData);
      setAchievements(achData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogHydration = async (amount) => {
    try {
      await progressApi.logHydration(amount);
      const newHydration = await progressApi.getTodayHydration();
      setHydration(newHydration);
      toast({
        title: language === 'en' ? 'Water logged!' : '¡Agua registrada!',
        description: `+${amount}ml`
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { icon: Home, label: language === 'en' ? 'Dashboard' : 'Inicio', path: '/dashboard' },
    { icon: Dumbbell, label: language === 'en' ? 'Programs' : 'Programas', path: '/programs' },
    { icon: Utensils, label: language === 'en' ? 'Nutrition' : 'Nutrición', path: '/nutrition' },
    { icon: Heart, label: language === 'en' ? 'Wellbeing' : 'Bienestar', path: '/wellbeing' },
    { icon: Trophy, label: language === 'en' ? 'Achievements' : 'Logros', path: '/achievements' },
    { icon: User, label: language === 'en' ? 'Profile' : 'Perfil', path: '/profile' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1f1f] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
      </div>
    );
  }

  const unlockedAchievements = achievements.filter(a => a.unlocked).length;

  return (
    <div className="min-h-screen bg-[#0d1f1f]">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-gray-900/95 backdrop-blur-xl border-r border-gray-800 z-50
        transform transition-transform duration-300 lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-6">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 mb-8">
            <div className="text-xl font-bold text-white tracking-tight">
              <span className="text-white">THE</span>
              <span className="text-emerald-400 ml-1">WALLNESS</span>
            </div>
            <div className="h-6 w-1 bg-emerald-400"></div>
          </Link>

          {/* User info */}
          <div className="bg-gray-800/50 rounded-2xl p-4 mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-white font-semibold">
                  {user?.profile?.first_name || 'Usuario'}
                </p>
                <p className="text-gray-400 text-sm">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="flex items-center space-x-3 px-4 py-3 rounded-xl text-gray-400 hover:text-white hover:bg-gray-800/50 transition-all"
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>
        </div>

        {/* Logout */}
        <div className="absolute bottom-6 left-6 right-6">
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-gray-400 hover:text-red-400 hover:bg-red-500/10"
          >
            <LogOut className="w-5 h-5 mr-3" />
            {language === 'en' ? 'Logout' : 'Cerrar Sesión'}
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen">
        {/* Mobile header */}
        <header className="lg:hidden sticky top-0 bg-gray-900/95 backdrop-blur-xl border-b border-gray-800 p-4 z-30">
          <div className="flex items-center justify-between">
            <button onClick={() => setSidebarOpen(true)} className="text-white p-2">
              <Menu className="w-6 h-6" />
            </button>
            <div className="text-xl font-bold text-white">
              <span>THE</span>
              <span className="text-emerald-400 ml-1">WALLNESS</span>
            </div>
            <div className="w-10" />
          </div>
        </header>

        <div className="p-6 lg:p-8">
          {/* Welcome */}
          <div className="mb-8">
            <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">
              {language === 'en' ? 'Hello' : 'Hola'}, {user?.profile?.first_name || 'Atleta'}! 👋
            </h1>
            <p className="text-gray-400">
              {language === 'en' 
                ? "Let's build your wall today, brick by brick"
                : 'Construyamos tu muro hoy, ladrillo por ladrillo'
              }
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard
              icon={Flame}
              value={dashboard?.streak_days || 0}
              label={language === 'en' ? 'Day Streak' : 'Días de Racha'}
              color="orange"
            />
            <StatCard
              icon={Target}
              value={dashboard?.total_workouts || 0}
              label={language === 'en' ? 'Workouts' : 'Entrenamientos'}
              color="emerald"
            />
            <StatCard
              icon={Trophy}
              value={`${unlockedAchievements}/${achievements.length}`}
              label={language === 'en' ? 'Achievements' : 'Logros'}
              color="yellow"
            />
            <StatCard
              icon={TrendingUp}
              value={`${Math.round(dashboard?.weekly_goal_progress || 0)}%`}
              label={language === 'en' ? 'Weekly Goal' : 'Meta Semanal'}
              color="blue"
            />
          </div>

          {/* Main Grid */}
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Hydration Tracker */}
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-3xl p-6 border border-gray-800">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center">
                  <Droplets className="w-6 h-6 text-blue-400 mr-2" />
                  {language === 'en' ? 'Hydration' : 'Hidratación'}
                </h2>
              </div>

              <div className="relative mb-6">
                <div className="w-32 h-32 mx-auto relative">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="12"
                      fill="none"
                      className="text-gray-800"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="12"
                      fill="none"
                      strokeDasharray={`${(hydration?.percentage || 0) * 3.52} 352`}
                      strokeLinecap="round"
                      className="text-blue-500 transition-all duration-500"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold text-white">
                      {Math.round(hydration?.percentage || 0)}%
                    </span>
                    <span className="text-xs text-gray-400">
                      {hydration?.current_ml || 0}/{hydration?.target_ml || 2000}ml
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {[150, 250, 500].map((amount) => (
                  <Button
                    key={amount}
                    onClick={() => handleLogHydration(amount)}
                    variant="outline"
                    className="border-blue-500/30 text-blue-400 hover:bg-blue-500/20"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {amount}ml
                  </Button>
                ))}
              </div>
            </div>

            {/* Today's Progress */}
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-3xl p-6 border border-gray-800">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center">
                <Calendar className="w-6 h-6 text-emerald-400 mr-2" />
                {language === 'en' ? "Today's Progress" : 'Progreso de Hoy'}
              </h2>

              <div className="space-y-4">
                <ProgressItem
                  label={language === 'en' ? 'Workouts' : 'Entrenamientos'}
                  value={dashboard?.today?.workouts_completed || 0}
                  target={user?.profile?.goals?.weekly_workout_target || 3}
                  unit=""
                />
                <ProgressItem
                  label={language === 'en' ? 'Calories' : 'Calorías'}
                  value={dashboard?.today?.calories_burned || 0}
                  target={300}
                  unit="kcal"
                />
                <ProgressItem
                  label={language === 'en' ? 'Water' : 'Agua'}
                  value={hydration?.current_ml || 0}
                  target={hydration?.target_ml || 2000}
                  unit="ml"
                />
              </div>

              <Link to="/programs">
                <Button className="w-full mt-6 bg-emerald-500 hover:bg-emerald-600">
                  <Dumbbell className="w-4 h-4 mr-2" />
                  {language === 'en' ? 'Start Workout' : 'Comenzar Entrenamiento'}
                </Button>
              </Link>
            </div>

            {/* Featured Programs */}
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-3xl p-6 border border-gray-800">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center">
                <Dumbbell className="w-6 h-6 text-emerald-400 mr-2" />
                {language === 'en' ? 'Programs' : 'Programas'}
              </h2>

              <div className="space-y-3">
                {programs.slice(0, 3).map((program) => (
                  <Link
                    key={program.id}
                    to={`/programs/${program.id}`}
                    className="block bg-gray-800/50 rounded-xl p-4 hover:bg-gray-800 transition-colors group"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-white font-medium group-hover:text-emerald-400 transition-colors">
                          {language === 'en' ? program.name_en : program.name_es}
                        </h3>
                        <p className="text-gray-500 text-sm">
                          {program.duration_weeks} {language === 'en' ? 'weeks' : 'semanas'} • {program.difficulty}
                        </p>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <TrendingUp className="w-4 h-4 text-emerald-400" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              <Link to="/programs">
                <Button variant="outline" className="w-full mt-4 border-gray-700 text-gray-300 hover:text-white">
                  {language === 'en' ? 'View All Programs' : 'Ver Todos los Programas'}
                </Button>
              </Link>
            </div>
          </div>

          {/* Progress Charts */}
          <div className="mt-6">
            <ProgressCharts language={language} />
          </div>

          {/* Recent Achievements */}
          <div className="mt-6 bg-gray-900/50 backdrop-blur-sm rounded-3xl p-6 border border-gray-800">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center">
              <Trophy className="w-6 h-6 text-yellow-400 mr-2" />
              {language === 'en' ? 'Achievements' : 'Logros'}
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {achievements.map((achievement) => (
                <div
                  key={achievement.id}
                  className={`p-4 rounded-xl text-center transition-all ${
                    achievement.unlocked 
                      ? 'bg-yellow-500/20 border border-yellow-500/30' 
                      : 'bg-gray-800/50 border border-gray-700/50 opacity-50'
                  }`}
                >
                  <div className={`w-12 h-12 mx-auto mb-2 rounded-full flex items-center justify-center ${
                    achievement.unlocked ? 'bg-yellow-500/30' : 'bg-gray-700/50'
                  }`}>
                    {achievement.unlocked ? (
                      <Check className="w-6 h-6 text-yellow-400" />
                    ) : (
                      <Trophy className="w-6 h-6 text-gray-500" />
                    )}
                  </div>
                  <p className={`text-sm font-medium ${achievement.unlocked ? 'text-white' : 'text-gray-500'}`}>
                    {language === 'en' ? achievement.name_en : achievement.name_es}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {achievement.points} pts
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

const StatCard = ({ icon: Icon, value, label, color }) => {
  const colors = {
    orange: 'bg-orange-500/20 text-orange-400',
    emerald: 'bg-emerald-500/20 text-emerald-400',
    yellow: 'bg-yellow-500/20 text-yellow-400',
    blue: 'bg-blue-500/20 text-blue-400'
  };

  return (
    <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-4 border border-gray-800">
      <div className={`w-10 h-10 rounded-xl ${colors[color]} flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-gray-500 text-sm">{label}</p>
    </div>
  );
};

const ProgressItem = ({ label, value, target, unit }) => {
  const percentage = Math.min((value / target) * 100, 100);
  
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-400">{label}</span>
        <span className="text-white">{value}{unit} / {target}{unit}</span>
      </div>
      <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
        <div 
          className="h-full bg-emerald-500 rounded-full transition-all duration-500"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

export default Dashboard;
